/**
 * SitePick front — localStorage 安全ラッパ（docs/06 §2 / §8）
 *
 * localStorage が使えない環境（プライベートブラウズ / quota / SecurityError）でも
 * 例外を投げず、現在タブ内のメモリ Map に degrade する。
 * ここでは「文字列の read / write / remove」と「JSON の parse / stringify」だけを担う。
 * ドメイン知識（favorites / compare の形）は持たない。
 */

const memory = new Map();

let persistent = null;

/**
 * localStorage が実際に読み書きできるか一度だけ検査する。
 *
 * @returns {boolean}
 */
export function isPersistent() {
  if (persistent !== null) {
    return persistent;
  }
  try {
    const probe = "__sitepick_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    persistent = true;
  } catch {
    persistent = false;
  }
  return persistent;
}

/**
 * 生文字列を読む。存在しない / 失敗時は null。
 *
 * @param {string} key
 * @returns {string|null}
 */
export function readRaw(key) {
  if (isPersistent()) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      /* fall through to memory */
    }
  }
  return memory.has(key) ? memory.get(key) : null;
}

/**
 * 生文字列を書く。失敗しても投げない。
 *
 * @param {string} key
 * @param {string} value
 * @returns {boolean} 永続化できたら true（メモリのみは false）
 */
export function writeRaw(key, value) {
  if (isPersistent()) {
    try {
      window.localStorage.setItem(key, value);
      memory.set(key, value);
      return true;
    } catch {
      /* quota 等。メモリへ */
    }
  }
  memory.set(key, value);
  return false;
}

/**
 * キーを削除する。
 *
 * @param {string} key
 * @returns {void}
 */
export function removeKey(key) {
  if (isPersistent()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  memory.delete(key);
}

/**
 * JSON を読む。壊れていたら fallback を返す。
 *
 * @param {string} key
 * @param {*} fallback
 * @returns {*}
 */
export function readJSON(key, fallback) {
  const raw = readRaw(key);
  if (raw === null || raw === "") {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
}

/**
 * JSON を書く。
 *
 * @param {string} key
 * @param {*} value
 * @returns {boolean}
 */
export function writeJSON(key, value) {
  try {
    return writeRaw(key, JSON.stringify(value));
  } catch {
    return false;
  }
}
